import {Component} from '@angular/core';
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from "@angular/forms";
import {FormTypes, User} from 'src/app/shared/forms/models/constants';
import {FormElementComponent} from "../../../shared/forms/form-element/form-element.component";
import {InputSwitchModule} from 'primeng/inputswitch';
import {DatePipe, NgIf} from "@angular/common";
import {TranslatePipe} from "@ngx-translate/core";
import {Button} from "primeng/button";
import {IdCardComponent} from "../../../shared/components/id-card/id-card.component";
import {UserService} from "../../../shared/services/user.service";
import {UserProperties, UserPropertiesService} from "../../services/user-properties.service";
import {SignatureImageCropperComponent} from "./signature-image-cropper/signature-image-cropper.component";

@Component({
    selector: 'app-visual-signature',
    standalone: true,
    imports: [
        ReactiveFormsModule,
        FormElementComponent,
        InputSwitchModule,
        TranslatePipe,
        Button,
        IdCardComponent,
        SignatureImageCropperComponent,
        NgIf,
    ],
    providers: [DatePipe],
    templateUrl: './visual-signature.component.html',
    styleUrl: './visual-signature.component.scss'
})
export class VisualSignatureComponent {


    currentTime: string | null = '';
    signType!: string;

    readonly FormTypes = FormTypes;

    // signature image file upload variables and methods
    selectedFile: File | null = null;
    signatureImageProperty!: UserProperties;

    formGroup = new FormGroup({
        signatureName: new FormControl(''),
        name: new FormControl('', [Validators.maxLength(40)]),
        optionalLine1: new FormControl(''),
        optionalLine2: new FormControl(''),
        imgFile: new FormControl(),
        dateCheck: new FormControl(false)
    });


    constructor(private datePipe: DatePipe,
                private userService: UserService,
                private userPropertiesService: UserPropertiesService) {
    }

    isUserPropertiesArrLength: boolean = false;

    ngOnInit() {
        this.userService.getCurrentUser().then((res: User) => {
            console.log(res)
            this.formGroup.get('name')?.setValue(`${res.firstname} ${res.lastname}`);
        });

        this.userPropertiesService.getAllByGroup('stamp').subscribe({
            next: (values: UserProperties[]) => {

                console.log(values);

                if (values.length > 0) {
                    this.isUserPropertiesArrLength = true;
                }

                values.forEach((value, index) => {

                    const key = value.userPropertyKey;
                    const val = value.userPropertyValue;

                    switch (key) {
                        case "sipo.stamp.name":
                            this.formGroup.controls.name.setValue(val);
                            break;
                        case "sipo.stamp.line1":
                            this.formGroup.controls.optionalLine1.setValue(val);
                            break;
                        case "sipo.stamp.line2":
                            this.formGroup.controls.optionalLine2.setValue(val);
                            break;
                        case "sipo.stamp.image":
                            //this.formGroup.controls.imgFile.setValue(val);
                            this.signatureImageProperty = value;
                            break;
                        case "sipo.stamp.time":
                            if (value.userPropertyValue.length > 0) {
                                this.formGroup.controls.dateCheck.setValue(true);
                                this.currentTime = value.userPropertyValue;
                            }
                            break;
                    }
                })
            },
            error: err => {
                console.error(err);
            }
        })

        this.userPropertiesService.getByKey('sipo.settings.evidence').subscribe(res => {
            this.signType = res.userPropertyValue;
        })
    }

    countNameChars() {
        const adjustedName = this.formGroup.get('name')?.value?.slice(0, 35);
        if (adjustedName && adjustedName.length > 0) {
            this.formGroup.get('name')?.setValue(adjustedName);
        }

        const charAmount = this.formGroup.value.name?.length;
        let dividerNumber = 20;
        if (charAmount) {
            if (charAmount > 15) {
                dividerNumber = 20;
            } else {
                dividerNumber = 15;
            }
        }

        return dividerNumber;
    }

    toggleTime(): void {
        const now = this.datePipe.transform(new Date(), 'dd.MM.yyyy, HH:mm:ss');
        if (this.formGroup.value.dateCheck) {
            this.currentTime = now;
        } else {
            this.currentTime = '';
        }
    }


    // the boolean variable "isImageCropperDisplaying" and "toggleImageCropper" event is used to control displaying of image cropper component
    isImageCropperDisplaying: boolean = false;

    toggleImageCropper() {
        this.isImageCropperDisplaying = !this.isImageCropperDisplaying;
    }


    submitted: boolean = true;

    submit() {
        this.submitted = false;
        const name = this.formGroup.controls.name.getRawValue() ?? "";
        const line1 = this.formGroup.controls.optionalLine1.getRawValue() ?? "";
        const line2 = this.formGroup.controls.optionalLine2.getRawValue() ?? "";


        const payload: UserProperties[] = [
            {
                type: "string",
                userPropertyKey: "sipo.stamp.name",
                userPropertyValue: name,
                propertyGroup: "stamp"
            },
            {
                type: "string",
                userPropertyKey: "sipo.stamp.line1",
                userPropertyValue: line1,
                propertyGroup: "stamp"
            },
            {
                type: "string",
                userPropertyKey: "sipo.stamp.line2",
                userPropertyValue: line2,
                propertyGroup: "stamp"
            },
            {
                type: "string",
                userPropertyKey: "sipo.stamp.image",
                userPropertyValue: this.signatureImageProperty.userPropertyValue ?? "",
                propertyGroup: "stamp"
            },
            {
                type: "string",
                userPropertyKey: "sipo.stamp.time",
                userPropertyValue: this.currentTime ?? "",
                propertyGroup: "stamp"
            }
        ];

        this.userPropertiesService.createBulk(payload).subscribe({
            next: (values: UserProperties[]) => {
                setTimeout(() => {
                    this.submitted = true;
                }, 1500)
            },
            error: err => {
                console.error(err);
            }
        });
    }

    receiveSignatureImage(blob: Blob | null) {
        const reader = new FileReader();
        reader.onloadend = () => {
            this.signatureImageProperty.userPropertyValue = reader.result as string;
        };

        if (blob) {
            reader.readAsDataURL(blob);
        }

        this.isImageCropperDisplaying = false;
    }

    // the Event type variable "imageSelectionEvent" is catched with input in the signature-image-cropper-component and used in it. The method "onFileSelected" is used to select file, declare it to imageSelectionEvent and then display signature-image-cropper-component
    imageSelectionEvent: Event | null = null;

    onFileSelected(event: Event) {
        this.imageSelectionEvent = event;
        this.isImageCropperDisplaying = true;
    }

    deleteSignatureImage() {
        this.signatureImageProperty.userPropertyValue = '';
    }

}
